/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperExperience } from '@/scapi';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

/**
 * ID of the inert JSON script (`type="application/json"`, never executed) that lists the Page Designer component
 * types present in the server-rendered HTML. The client entry preloads those component modules before hydrating,
 * so a lazily-registered component doesn't suspend mid-hydration and get its server HTML discarded (layout shift).
 */
export const PD_COMPONENT_TYPES_ELEMENT_ID = 'sfnext-pd-component-types';

function isPage(value: unknown): value is PageWithComponentData {
    return (
        typeof value === 'object' &&
        value !== null &&
        !(value instanceof Promise) &&
        Array.isArray((value as PageWithComponentData).regions)
    );
}

function collectFromRegions(regions: ShopperExperience.schemas['Region'][] | undefined, types: Set<string>): void {
    for (const region of regions ?? []) {
        for (const component of region.components ?? []) {
            if (component.typeId) types.add(component.typeId);
            collectFromRegions(component.regions, types);
        }
    }
}

/**
 * Collects the component type IDs of every *resolved* Page Designer page found in route loader data. Deferred
 * (Promise) pages are skipped: they stream in after hydration starts and suspend inside their own boundary. When a
 * page declares `renderedRegionIds`, only those top-level regions are considered.
 */
export function collectComponentTypeIds(loaderDataList: unknown[]): string[] {
    const types = new Set<string>();
    for (const loaderData of loaderDataList) {
        if (typeof loaderData !== 'object' || loaderData === null) continue;
        for (const value of Object.values(loaderData)) {
            if (!isPage(value)) continue;
            const { renderedRegionIds } = value;
            const regions = renderedRegionIds
                ? value.regions?.filter((region) => renderedRegionIds.includes(region.id))
                : value.regions;
            collectFromRegions(regions, types);
        }
    }
    return [...types].sort();
}

/** Reads the component type list written by the server (client only). Returns [] when absent or malformed. */
export function readServerRenderedComponentTypes(doc: Document = document): string[] {
    try {
        const parsed: unknown = JSON.parse(doc.getElementById(PD_COMPONENT_TYPES_ELEMENT_ID)?.textContent || '[]');
        return Array.isArray(parsed) ? parsed.filter((type): type is string => typeof type === 'string') : [];
    } catch {
        return [];
    }
}
